import { useContext, useEffect, useRef, useState, useMemo, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { produce } from "immer";
// Material Dashboard 2 React components
import { Autocomplete, Button, Checkbox, Link, TextField, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import dayjs from "dayjs";
import { createMRTColumnHelper } from "material-react-table";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useDispatch, useSelector } from "react-redux";
import MDAlert from "../../components/MDAlert";
import MDTypography from "../../components/MDTypography";
import { AuthContext } from "../../context";
import { saveDate } from "../../dateSlice";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";
// import { tenant } from "../../tenant"; // No longer needed for REST endpoints
import toast from "react-hot-toast";
import GenericTable from "../genericTable";
import "./CalendarStyles.css";
// GraphQL imports removed - now using REST endpoints
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MDButton from "components/MDButton";
import { padding } from "@mui/system";

const JOURNAL_HEADER_HEIGHT = 130;

const NON_OBLIQUE_HEAD_CELL_SX = {
	height: JOURNAL_HEADER_HEIGHT,
	"& .Mui-TableHeadCell-Content": {
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-end",
		height: "100%",
	},
};

const buildObliqueColumnMeta = (label, width = 140, options = {}) => {
	const { headSx = {}, bodySx = {}, headerHeight, enableHeadFlex = false } = options;
	const headerCellHeight = headerHeight ?? JOURNAL_HEADER_HEIGHT;
	return {
		header: label,
		size: width,
		width,
		minSize: width,
		maxSize: width,
		enableColumnFilter: false,
		enableSorting: false,
		enableColumnActions: false,
		muiTableHeadCellProps: {
			sx: {
				...(enableHeadFlex && {
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-end",
				}),
				maxWidth: width,
				minWidth: width,
				height: headerCellHeight,
				minHeight: headerCellHeight,
				maxHeight: headerCellHeight,
				px: 0.5,
				py: 2,
				overflow: "visible",
				"& .Mui-TableHeadCell-Content": {
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-start",
					justifyContent: "flex-end",
					height: "100%",
					overflow: "visible",
				},
				"& .Mui-TableHeadCell-Content-Labels": {
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-start",
					justifyContent: "flex-end",
					height: "100%",
					textWrap: "wrap",
					overflow: "visible",
					paddingLeft: "1rem",
				},
				"& .Mui-TableHeadCell-Content-Wrapper": {
					display: "inline-flex",
					transform: "rotate(-55deg)",
					transformOrigin: "left bottom",
					fontSize: "0.75rem",
					fontWeight: 600,
					textTransform: "uppercase",
					letterSpacing: "0.03em",
					whiteSpace: "nowrap",
				},
				...headSx,
			},
		},
		muiTableBodyCellProps: {
			sx: {
				maxWidth: width,
				minWidth: width,
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				...bodySx,
			},
		},
	};
};

function JournalAppeal() {
	const authContext = useContext(AuthContext);
	const navigate = useNavigate();
	const [errorField, setErrorField] = useState([]);
	const [errorHoursField, setErrorHoursField] = useState([]);
	const [weatherOptions, setWeatherOptions] = useState([]);
	const [appealData, setAppealData] = useState([]);
	const [appeals, setAppeals] = useState([]);

	const dispatch = useDispatch();
	const savedState = useSelector((state) => {
		return state.date;
	});
	const [state, setState] = useState(savedState);

	const [motivations, setMotivations] = useState([]);
	const columnHelper = createMRTColumnHelper();
	const [viewTable, setViewTable] = useState(false);
	const [loading, setLoading] = useState(false);

	const [selectWeather, setSelectWeather] = useState(null);
	const [errorExtraHoursField, setErrorExtraHoursField] = useState([]);

	const [suppliers, setSuppliers] = useState([]);
	const [filteredSuppliers, setFilteredSuppliers] = useState([]);
	// Refs for debouncing various operations
	const cigNotesDebounceRef = useRef({});
	const hoursDebounceRef = useRef({});
	const presenceDebounceRef = useRef({});
	const mealDebounceRef = useRef({});
	const motivationDebounceRef = useRef({});

	const [datesAlreadyFilled, setDatesAlreadyFilled] = useState([]);
	const [activeStartDate, setActiveStartDate] = useState(new Date());

	// Memoized values
	const selectedDate = useMemo(() => state?.date, [state?.date]);
	const formattedDate = useMemo(() => dayjs(selectedDate).format("YYYY-MM-DD"), [selectedDate]);
	const areaId = useMemo(() => localStorage.getItem("areaSelected"), []);
	const isSaturday = useMemo(() => new Date(selectedDate).getDay() === 6, [selectedDate]);

	// Memoized filtered data
	const filteredAppealData = useMemo(() => {
		if (filteredSuppliers.length > 0) {
			return appealData.filter((item) => filteredSuppliers.includes(item.supplier));
		}
		return appealData;
	}, [appealData, filteredSuppliers]);

	// Definiamo una funzione che limita la selezione delle date al massimo fino ad oggi
	const tileDisabled = useCallback(({ date }) => {
		const today = new Date();
		// Disabilita le date future
		return date > today;
	}, []);

	const handleSetNotification = useCallback((msg, severityType) => {
		toast[severityType](msg, {
			id: "journal-appeal-notification-toast",
		});
	}, []);

	const deleteResource = useCallback(
		async (endpoint) => {
			try {
				const headers = {};
				const params = {
					id: areaId,
					date: formattedDate,
				};
				const response = await apiService.delete(endpoint, params, headers);
				return response;
			} catch (error) {
				throw error;
			}
		},
		[areaId, formattedDate]
	);

	const getAppeals = useCallback(() => {
		apiService
			.get("/appeal-management/", {
				id: areaId,
				date: formattedDate,
			})
			.then((data) => {
				if (data && Array.isArray(data)) {
					setAppeals(data);
				}
			})
			.catch((error) => {
				toast.error(
					"Qualcosa è andato storto durante il caricamento delle presenze. Riprovare più tardi, se l'errore persiste contatti il supporto tecnico segnalando l'errore #44637439",
					{
						id: "journal-appeal-load-appeals-error-toast",
					}
				);
			});
	}, [areaId, formattedDate]);

	const handleDelete = useCallback(
		(endpoint) => {
			deleteResource(endpoint)
				.then((result) => {
					toast.success("Presenze eliminate con successo", {
						id: "journal-appeal-delete-success-toast",
					});
					setLoading(true);
					getAppeals();
				})
				.catch((error) => {
					toast.error(
						"Si è verificato un errore durante l'eliminazione delle presenze. Riprova più tardi se l'errore persiste contatta il supporto tecnico indicato l'errore #79843587",
						{
							id: "journal-appeal-delete-error-toast",
						}
					);
				});
		},
		[deleteResource, getAppeals]
	);

	const fetchAppeal = useCallback(() => {
		apiService
			.get(
				"/appeal-management/",
				{
					id: areaId,
					date: formattedDate,
					from_appeal_journal_screen: true,
				},
				{}
			)
			.then((data) => {
				setAppealData(data);
				const _suppliers = [...new Set(data.map((item) => item.supplier).filter(Boolean))];
				setSuppliers(_suppliers);
			})
			.catch((error) =>
				toast.error(
					"Errore durante il caricamento dei dati. Riprova più tardi se l'errore persiste contatti il supoorto tecnico indicato l'errore #004535",
					{
						id: "journal-appeal-load-error-toast",
					}
				)
			);
	}, [areaId, formattedDate]);

	const handleActiveStartDateChange = useCallback(({ activeStartDate, view }) => {
		setActiveStartDate(activeStartDate);
	}, []);

	// Debounced API calls
	const debouncedHoursUpdate = useCallback((key, data) => {
		if (hoursDebounceRef.current[key]) {
			clearTimeout(hoursDebounceRef.current[key]);
		}
		hoursDebounceRef.current[key] = setTimeout(() => {
			console.log("dataaaaaa", data);
			apiService
				.post("/appeal-management/", { "Content-Type": "application/json" }, data)
				.then(() => {
					toast.success("Ore salvate con successo!", {
						id: "journal-appeal-hours-success",
					});
				})
				.catch((error) => {
					console.log(error);
					toast.error("Errore durante la chiamata al server, codice d'errore #0852132@906", {
						id: "journal-appeal-hours-error",
					});
				});
			delete hoursDebounceRef.current[key];
		}, 300);
	}, []);

	const debouncedPresenceUpdate = useCallback((key, data) => {
		if (presenceDebounceRef.current[key]) {
			clearTimeout(presenceDebounceRef.current[key]);
		}
		presenceDebounceRef.current[key] = setTimeout(() => {
			apiService
				.post("/appeal-management/", { "Content-Type": "application/json" }, data)
				.then(() => {
					toast.success("Presenza aggiornata con successo!", {
						id: "journal-appeal-presence-success",
					});
				})
				.catch((error) => {
					toast.error("Errore durante l'aggiornamento della presenza", {
						id: "journal-appeal-presence-error",
					});
				});
			delete presenceDebounceRef.current[key];
		}, 200);
	}, []);

	const debouncedMealUpdate = useCallback((key, data) => {
		if (mealDebounceRef.current[key]) {
			clearTimeout(mealDebounceRef.current[key]);
		}
		mealDebounceRef.current[key] = setTimeout(() => {
			apiService
				.post("/appeal-management/", { "Content-Type": "application/json" }, data)
				.then(() => {
					const message = data.meal ? "Pasto salvato con successo!" : "Pasto tolto con successo!";
					toast.success(message, {
						id: "journal-appeal-meal-success",
					});
				})
				.catch((error) => {
					toast.error("Errore durante l'aggiornamento del pasto", {
						id: "journal-appeal-meal-error",
					});
				});
			delete mealDebounceRef.current[key];
		}, 200);
	}, []);

	const debouncedMotivationUpdate = useCallback((key, data) => {
		if (motivationDebounceRef.current[key]) {
			clearTimeout(motivationDebounceRef.current[key]);
		}
		motivationDebounceRef.current[key] = setTimeout(() => {
			apiService
				.post("/appeal-management/", { "Content-Type": "application/json" }, data)
				.then(() => {
					toast.success("Motivazione salvata con successo!", {
						id: "journal-appeal-motivation-success",
					});
				})
				.catch((error) => {
					toast.error("Errore durante il salvataggio della motivazione", {
						id: "journal-appeal-motivation-error",
					});
				});
			delete motivationDebounceRef.current[key];
		}, 200);
	}, []);

	useEffect(() => {
		const fetchDates = async () => {
			try {
				const response = await apiService.get("/appeal-management/dates", {
					id: localStorage.getItem("areaSelected"),
					month: dayjs(activeStartDate).format("MM"),
					year: dayjs(activeStartDate).format("YYYY"),
				});
				setDatesAlreadyFilled((prev) => {
					const allDates = [...prev, ...response.dates.map((date) => new Date(date))];
					const uniqueDatesMap = new Map();
					allDates.forEach((d) => {
						const key = d.toISOString().slice(0, 10);
						if (!uniqueDatesMap.has(key)) {
							uniqueDatesMap.set(key, d);
						}
					});
					return Array.from(uniqueDatesMap.values());
				});
			} catch (_) {}
		};

		fetchDates();
	}, [activeStartDate]);
	useEffect(() => {
		apiService
			.get("/motivations/")
			.then((data) => {
				if (data && Array.isArray(data)) setMotivations(data);
			})
			.catch((error) => {
				toast.error(
					"Qualcosa è andato storto durante il caricamento delle motivazioni. Riprovare più tardi, se l'errore persiste contatti il supporto tecnico segnalando l'errore #00637439",
					{
						id: "journal-appeal-load-motivations-error-toast",
					}
				);
			});
		apiService
			.get("/weather-types/")
			.then((data) => {
				if (data && Array.isArray(data)) setWeatherOptions(data);
			})
			.catch((error) => {
				toast.error(
					"Qualcosa è andato storto durante il caricamento delle tipologia di clima. Riprovare più tardi, se l'errore persiste contatti il supporto tecnico segnalando l'errore #8734539",
					{
						id: "journal-appeal-load-weather-error-toast",
					}
				);
			});
	}, []);

	useEffect(() => {
		getAppeals();
	}, [getAppeals, state.date]);

	useEffect(() => {
		if (appeals.length > 0) {
			setViewTable(true);
		} else {
			setLoading(false);
			setViewTable(false);
		}
	}, [appeals]);

	useEffect(() => {
		if (viewTable) {
			fetchAppeal();
			apiService
				.get(
					"/area-weather-management/",
					{
						id: localStorage.getItem("areaSelected"),
						date: dayjs(state.date).format("YYYY-MM-DD"),
					},
					{}
				)
				.then((data) => {
					if (data) setSelectWeather(data);
				})
				.catch((error) =>
					toast.error(
						"Errore durante il caricamento dei dati. Riprova più tardi se l'errore persiste contatti il supoorto tecnico indicato l'errore #0045423452@35",
						{
							id: "journal-appeal-load-weather-error-toast",
						}
					)
				);
		}
	}, [viewTable, fetchAppeal, state.date]);

	const handleChangeHours = useCallback(
		(e, row) => {
			const fieldName = e.target.getAttribute("data-field");
			const fieldValue = e.target.value === "" ? 0 : parseFloat(e.target.value) || 0;

			const isRecursiveCall = e.target.getAttribute("data-recursive") === "true";

			if (isRecursiveCall) {
				console.log("Recursive call, skipping", isRecursiveCall);
				return;
			}
			// Get current row data

			console.log("index", row?.index, appealData);

			// Check if appealData is loaded
			if (!appealData || appealData.length === 0) {
				console.warn("AppealData not loaded yet, skipping update");
				return;
			}

			const currentRowData = appealData[row?.index];
			if (!currentRowData) {
				console.error("Row data not found for index:", row?.index);
				return;
			}

			// Calculate new values first
			let hoursOfWork = parseFloat(currentRowData.hours_of_work) || 0;
			let hoursOfCig = parseFloat(currentRowData.hours_of_cig) || 0;
			let hoursOfPermission = parseFloat(currentRowData.hours_of_permission) || 0;

			// Update the field that was modified
			if (fieldName === "hours_of_work") {
				hoursOfWork = fieldValue;
			} else if (fieldName === "hours_of_cig") {
				hoursOfCig = fieldValue;
			} else if (fieldName === "hours_of_permission") {
				hoursOfPermission = fieldValue;
			}

			if (hoursOfWork > 8) {
				if (fieldName === "hours_of_cig") {
					hoursOfCig = fieldValue;
					hoursOfWork = 0;
				} else if (fieldName === "hours_of_permission") {
					hoursOfPermission = fieldValue;
					hoursOfWork = 0;
				}
			}

			if (!isSaturday) {
				if (hoursOfWork > 8) {
					hoursOfCig = 0;
					hoursOfPermission = 0;
				} else {
					const totalFixedHours = hoursOfCig + hoursOfPermission;
					if (totalFixedHours >= 8) {
						hoursOfWork = 0;
					} else if (fieldName === "hours_of_cig" || fieldName === "hours_of_permission") {
						hoursOfWork = 8 - totalFixedHours;
					}
				}
			}

			// Calculate extra hours
			const hoursOfExtraWork = isSaturday ? hoursOfWork : hoursOfWork > 8 ? hoursOfWork - 8 : 0;

			// Store final values for API call
			const finalHoursOfWork = hoursOfWork;
			const finalHoursOfCig = hoursOfCig;
			const finalHoursOfPermission = hoursOfPermission;

			// Update state with calculated values
			setAppealData(
				produce((draft) => {
					const rowData = draft[row?.index];
					if (rowData) {
						rowData.hours_of_work = hoursOfWork > 8 ? 8 : hoursOfWork;
						rowData.hours_of_cig = hoursOfCig;
						rowData.hours_of_permission = hoursOfPermission;
						rowData.hours_of_extra_work = hoursOfExtraWork;
					}
				})
			);

			// Validation and error handling
			if (fieldValue < 0) {
				setErrorHoursField((prev) => {
					if (!prev.includes(row.original.id)) {
						toast.warning("Le ore non possono essere negative");
						return [...prev, row.original.id];
					}
					return prev;
				});
				return;
			}

			// Remove errors if valid
			setErrorHoursField((prev) => prev.filter((item) => item !== row.original.id));

			// Debounced API call
			const debounceKey = `hours_${row.original.id}`;
			debouncedHoursUpdate(debounceKey, {
				id: areaId,
				date: formattedDate,
				hours_of_work: finalHoursOfWork > 8 ? 8 : finalHoursOfWork,
				hours_of_cig: finalHoursOfCig,
				hours_of_permission: finalHoursOfPermission,
				hours_of_extra_work: hoursOfExtraWork,
				user_id: row.original.id,
			});
		},
		[isSaturday, areaId, formattedDate, debouncedHoursUpdate, appealData]
	);

	// Optimized handleChangeExtrahours with debouncing
	const handleChangeExtrahours = useCallback(
		(event, row) => {
			const hoursOfExtraWork = event.target.value;
			setAppealData((prev) => {
				const newData = [...prev];
				newData[row?.index]["hours_of_extra_work"] = hoursOfExtraWork;
				return newData;
			});

			if (hoursOfExtraWork < 0) {
				setErrorExtraHoursField((prev) => [...prev, row.original.id]);
				return;
			}

			setErrorExtraHoursField((prev) => prev.filter((id) => id !== row.original.id));

			// Use debounced API call
			const debounceKey = `extra_hours_${row.original.id}`;
			debouncedHoursUpdate(debounceKey, {
				id: areaId,
				date: formattedDate,
				hours_of_extra_work: hoursOfExtraWork,
				user_id: row.original.id,
			});
		},
		[areaId, formattedDate, debouncedHoursUpdate]
	);

	// Optimized debounced function for CIG notes
	const debouncedSaveCigNotes = useCallback(
		(value, userId) => {
			if (cigNotesDebounceRef.current[userId]) {
				clearTimeout(cigNotesDebounceRef.current[userId]);
			}
			cigNotesDebounceRef.current[userId] = setTimeout(() => {
				try {
					apiService
						.post(
							"/appeal-management/",
							{
								"Content-Type": "application/json",
							},
							{
								id: areaId,
								date: formattedDate,
								cig_notes: value,
								user_id: userId,
							}
						)
						.then(() => {
							toast.success("Nota salvata con successo!", {
								id: "journal-appeal-cig-notes-success",
							});
						})
						.catch(() => {
							toast.error("Errore durante il salvataggio della nota", {
								id: "journal-appeal-cig-notes-error",
							});
						});
				} catch (error) {
					toast.error("Errore durante il salvataggio della nota", {
						id: "journal-appeal-cig-notes-error",
					});
				}
			}, 500);
		},
		[areaId, formattedDate]
	);

	useEffect(() => {
		setLoading(false);
	}, [appealData]);

	const columns = useMemo(
		() => [
			columnHelper.accessor((row) => `${row.name ?? ""} ${row.last_name ?? ""}`.trim(), {
				id: "full_name",
				header: "Nome e cognome",
				size: 160,
				muiTableHeadCellProps: { sx: NON_OBLIQUE_HEAD_CELL_SX },
				Cell: ({ row }) => (
					<MDTypography sx={{ fontSize: "0.9rem", whiteSpace: "nowrap" }}>
						{`${row.original.name ?? ""} ${row.original.last_name ?? ""}`.trim()}
					</MDTypography>
				),
			}),
			columnHelper.accessor("supplier", {
				header: "Fornitore",
				size: 100,
				muiTableHeadCellProps: { sx: NON_OBLIQUE_HEAD_CELL_SX },
				Cell: ({ cell }) => (
					<MDTypography sx={{ fontSize: "0.85rem", whiteSpace: "normal", wordBreak: "break-word" }}>
						{cell.getValue() || "-"}
					</MDTypography>
				),
			}),
			columnHelper.accessor("presence", {
				...buildObliqueColumnMeta("Presente", 85),
				enableColumnFilter: false,
				Cell: ({ row }) => {
					return (
						<PresenceCell
							row={row}
							areaId={areaId}
							formattedDate={formattedDate}
							setErrorField={setErrorField}
							setAppealData={setAppealData}
							debouncedPresenceUpdate={debouncedPresenceUpdate}
						/>
					);
				},
			}),
			columnHelper.accessor("meal", {
				...buildObliqueColumnMeta("Pasto", 85),
				enableColumnFilter: false,
				Cell: ({ row }) => {
					return (
						<MealCell
							row={row}
							areaId={areaId}
							formattedDate={formattedDate}
							setAppealData={setAppealData}
							debouncedMealUpdate={debouncedMealUpdate}
						/>
					);
				},
			}),
			columnHelper.accessor("hours_of_work", {
				...buildObliqueColumnMeta("Ore lavoro", 80),
				enableColumnFilter: false,
				Cell: ({ row }) => {
					return (
						<HoursTextField
							row={row}
							fieldName="hours_of_work"
							errorHoursField={errorHoursField}
							setAppealData={setAppealData}
							handleChangeHours={handleChangeHours}
						/>
					);
				},
			}),
			columnHelper.accessor("hours_of_cig", {
				...buildObliqueColumnMeta("Ore CIG", 80),
				enableColumnFilter: false,
				Cell: ({ row }) => {
					return (
						<HoursTextField
							row={row}
							fieldName="hours_of_cig"
							errorHoursField={errorHoursField}
							setAppealData={setAppealData}
							handleChangeHours={handleChangeHours}
						/>
					);
				},
			}),
			columnHelper.accessor("hours_in_other_area", {
				...buildObliqueColumnMeta("Ore altri cantieri", 100, {
					bodySx: { whiteSpace: "normal" },
				}),
				enableColumnFilter: false,
			}),
			columnHelper.accessor("hours_of_permission", {
				...buildObliqueColumnMeta("Ore permesso", 80),
				enableColumnFilter: false,
				Cell: ({ row }) => {
					return (
						<HoursTextField
							row={row}
							fieldName="hours_of_permission"
							errorHoursField={errorHoursField}
							setAppealData={setAppealData}
							handleChangeHours={handleChangeHours}
						/>
					);
				},
			}),
			columnHelper.accessor("hours_of_extra_work", {
				...buildObliqueColumnMeta("Ore straordinari", 80),
				enableColumnFilter: false,
				Cell: ({ row }) => {
					return (
						<TextField
							disabled={true}
							variant="outlined"
							value={row.original.hours_of_extra_work}
							onChange={(e) => handleChangeExtrahours(e, row)}
							type="number"
							error={errorExtraHoursField.includes(row.original.id)}
							sx={{ width: "100%" }}
						/>
					);
				},
			}),
			columnHelper.accessor("cig_notes", {
				header: "Note CIG",
				size: 220,
				enableColumnFilter: false,
				muiTableHeadCellProps: { sx: NON_OBLIQUE_HEAD_CELL_SX },
				Cell: ({ row }) => {
					return <CigNotesCell row={row} debouncedSaveCigNotes={debouncedSaveCigNotes} />;
				},
			}),
			columnHelper.accessor("motivation", {
				header: "Motivazione",
				enableColumnFilter: false,
				size: 220,
				muiTableHeadCellProps: { sx: NON_OBLIQUE_HEAD_CELL_SX },
				Cell: ({ row }) => {
					return (
						<MotivationCell
							row={row}
							motivations={motivations}
							areaId={areaId}
							formattedDate={formattedDate}
							errorField={errorField}
							debouncedMotivationUpdate={debouncedMotivationUpdate}
						/>
					);
				},
			}),
		],
		[
			columnHelper,
			errorHoursField,
			errorExtraHoursField,
			errorField,
			motivations,
			areaId,
			formattedDate,
			handleChangeHours,
			handleChangeExtrahours,
			debouncedSaveCigNotes,
			debouncedPresenceUpdate,
			debouncedMealUpdate,
			debouncedMotivationUpdate,
		]
	);

	const handleDateChange = useCallback(
		(newDate) => {
			setState((prevState) => ({ ...prevState, date: newDate }));
			dispatch(saveDate({ date: newDate }));
			setViewTable(false);
			setLoading(true);
			setIsCalendarOpen(false);
		},
		[dispatch]
	);

	const isMarked = useCallback(
		(date) => {
			return datesAlreadyFilled.some(
				(marked) =>
					marked.getFullYear() === date.getFullYear() &&
					marked.getMonth() === date.getMonth() &&
					marked.getDate() === date.getDate()
			);
		},
		[datesAlreadyFilled]
	);

	const handleSupplierFilter = useCallback((supplier) => {
		setFilteredSuppliers((prev) => {
			if (prev.includes(supplier)) {
				return prev.filter((s) => s !== supplier);
			} else {
				return [...prev, supplier];
			}
		});
	}, []);

	const handleCreateAppeals = useCallback(() => {
		setIsCalendarOpen(false);
		setViewTable(true);
		setLoading(true);
	}, []);

	const handleToggleCalendar = useCallback(() => {
		setIsCalendarOpen((prev) => !prev);
	}, []);

	const handleDeleteAppeals = useCallback(() => {
		if (
			window.confirm(
				"Verranno cancellati tutti i dati relativi alle presenze del giorno selezionato, quindi le attività, i documenti del giorno e i pasti registrati. Sei sicuro di voler procedere?"
			)
		) {
			handleDelete("/appeal-management/");
		}
	}, [handleDelete]);

	const handleWeatherChange = useCallback(
		(event, newValue) => {
			apiService
				.post(
					"/area-weather-management/",
					{
						"Content-Type": "application/json",
					},
					{
						id: areaId,
						date: formattedDate,
						weather_id: newValue?.id,
					}
				)
				.then((data) => {
					toast.success("Meteo registrato con successo!", {
						id: "journal-appeal-load-error-toast",
					});
					setSelectWeather(newValue || null);
				})
				.catch((error) => {
					toast.error("Errore durante la chiamata al server, codice d'errore #06500@32476", {
						id: "journal-appeal-load-error-toast",
					});
				});
		},
		[areaId, formattedDate]
	);

	const [isCalendarOpen, setIsCalendarOpen] = useState(false);

	// Cleanup function for debounce timers
	useEffect(() => {
		return () => {
			// Clear all debounce timers on unmount
			const cigNotes = cigNotesDebounceRef.current;
			const hours = hoursDebounceRef.current;
			const presence = presenceDebounceRef.current;
			const meal = mealDebounceRef.current;
			const motivation = motivationDebounceRef.current;

			Object.values(cigNotes).forEach(clearTimeout);
			Object.values(hours).forEach(clearTimeout);
			Object.values(presence).forEach(clearTimeout);
			Object.values(meal).forEach(clearTimeout);
			Object.values(motivation).forEach(clearTimeout);
		};
	}, []);

	return authContext.hasAuth("VIEW_USERAPPEAL") ? (
		<>
			<DashboardLayout>
				<DashboardNavbar />
				<center>
					<div className="calendar-container">
						<div
							style={{
								display: !isCalendarOpen ? "none" : "block",
							}}
						>
							<Calendar
								onChange={handleDateChange}
								value={state.date}
								tileDisabled={tileDisabled}
								maxDate={new Date()}
								className="custom-calendar"
								onActiveStartDateChange={handleActiveStartDateChange}
								tileContent={(date, view) => {
									if (isMarked(date.date)) {
										return <div className="circle-marker"></div>;
									}
									return null;
								}}
							/>
						</div>
						<MDBox
							sx={{
								mt: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "16px",
								flexWrap: "wrap",
								marginBottom: "30px",
							}}
						>
							<Button
								variant="contained"
								color="primary"
								sx={{
									minWidth: "120px",
									color: "#ffffff",
								}}
								onClick={handleToggleCalendar}
							>
								{isCalendarOpen ? "Chiudi calendario" : "Apri calendario"}
							</Button>
							<MDTypography fontWeight="bold" sx={{ textTransform: "uppercase" }}>
								{state?.date?.toLocaleDateString("it-IT", {
									weekday: "long",
									year: "numeric",
									month: "2-digit",
									day: "2-digit",
								})}
							</MDTypography>
						</MDBox>
					</div>
				</center>

				<MDBox
					style={{
						width: "100%",
						marginTop: "20px",
						display: "flex",
						gap: "20px",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Autocomplete
						options={weatherOptions}
						getOptionLabel={(option) => (option?.name ? option.name : "Nessun meteo")}
						value={selectWeather}
						renderInput={(params) => <TextField {...params} label="Meteo" />}
						isOptionEqualToValue={(option, value) => {
							return value === option.id;
						}}
						onChange={handleWeatherChange}
						sx={{ mt: 2, mb: 2, width: "250px" }}
					/>

					<MDButton
						href={`/journal-activity`}
						underline="none"
						color="secondary"
						variant="contained"
						sx={{
							backgroundColor: "#333232",
							color: "#ffffff",
						}}
					>
						vai alle attività giornaliere
						<ArrowForwardIcon />
					</MDButton>
					{authContext.hasAuth("DELETE_USERAPPEAL") && (
						<Button
							variant="contained"
							color="primary"
							sx={{
								backgroundColor: "#800000",
								alignSelf: "center",
								minWidth: "100px",
								color: "#ffffff",
							}}
							onClick={handleDeleteAppeals}
						>
							Cancella le presenze
						</Button>
					)}
				</MDBox>
				<MDBox>
					{viewTable && !loading ? (
						<>
							<MDBox sx={{ marginBottom: "20px" }}>
								<MDTypography
									variant="h6"
									sx={{
										marginBottom: "10px",
										textAlign: "center",
									}}
								>
									Filtra fornitori
								</MDTypography>
								<MDBox
									sx={{
										display: "flex",
										border: "1px solid #1976d2",
										borderRadius: "10px",
										padding: "15px",
										overflowX: "auto",
										gap: "10px",
										"&::-webkit-scrollbar": {
											height: "8px",
										},
										"&::-webkit-scrollbar-track": {
											backgroundColor: "#f1f1f1",
											borderRadius: "4px",
										},
										"&::-webkit-scrollbar-thumb": {
											backgroundColor: "#888",
											borderRadius: "4px",
										},
										"&::-webkit-scrollbar-thumb:hover": {
											backgroundColor: "#555",
										},
									}}
								>
									{[...suppliers].map((supplier, i) => (
										<Button
											key={i}
											variant={filteredSuppliers.includes(supplier) ? "contained" : "outlined"}
											color="primary"
											sx={{
												minWidth: "120px",
												whiteSpace: "nowrap",
												backgroundColor: filteredSuppliers.includes(supplier) ? "#1976d2" : "#ffffff",
												color: filteredSuppliers.includes(supplier) ? "#ffffff" : "#1976d2",
												borderColor: "#1976d2",
												"&:hover": {
													backgroundColor: filteredSuppliers.includes(supplier) ? "#1565c0" : "#f5f5f5",
												},
											}}
											onClick={() => handleSupplierFilter(supplier)}
										>
											{supplier}
										</Button>
									))}
								</MDBox>
							</MDBox>
							<GenericTable
								sortingKey={null}
								authModelName={"USERAPPEAL"}
								disableButton={true}
								columns={columns}
								data={filteredAppealData}
								headerHeight={JOURNAL_HEADER_HEIGHT}
							/>
						</>
					) : !loading ? (
						authContext.hasAuth("ADD_USERAPPEAL") ? (
							<center>
								<Button
									variant="contained"
									color="primary"
									sx={{
										mt: 2,
										alignSelf: "center",
										minWidth: "100px",
										color: "#ffffff",
										marginBottom: "30px",
									}}
									onClick={handleCreateAppeals}
								>
									Crea le presenze
								</Button>
							</center>
						) : (
							<center>
								<MDTypography fontWeight="bold" style={{ marginTop: "10px" }} textTransform="uppercase">
									Non hai il permesso per creare le presenze
								</MDTypography>
							</center>
						)
					) : (
						<></>
					)}
				</MDBox>
			</DashboardLayout>
		</>
	) : (
		<DashboardLayout>
			<MDAlert color="error" dismissible>
				<MDTypography variant="body2">You don't have permission to access this page</MDTypography>
			</MDAlert>
		</DashboardLayout>
	);
}

// Optimized Cell Components with debouncing
const PresenceCell = memo(({ row, areaId, formattedDate, setErrorField, setAppealData, debouncedPresenceUpdate }) => {
	const isCompliant = row?.original?.documents_compliant ?? true;

	// Disabilita se documenti non in regola
	const shouldDisable = !isCompliant;

	const handlePresenceChange = useCallback(
		(event, value) => {
			if (shouldDisable) return;

			if (!value) {
				setErrorField((prev) => prev.filter((item) => item !== row.original.id));

				// Salva le ore prima di disattivare la presenza
				const debounceKey = `presence_${row.original.id}`;
				debouncedPresenceUpdate(debounceKey, {
					id: areaId,
					date: formattedDate,
					presence: value,
					motivation: row?.original?.motivation?.id,
					user_id: row.original.id,
					// Mantieni le ore anche quando disattivi la presenza
					hours_of_work: row.original.hours_of_work || 0,
					hours_of_cig: row.original.hours_of_cig || 0,
					hours_of_permission: row.original.hours_of_permission || 0,
					hours_of_extra_work: row.original.hours_of_extra_work || 0,
				});
			} else {
				setAppealData(
					produce((draft) => {
						draft[row?.index].presence = value;
						draft[row?.index].motivation = null;
						// Mantieni le ore quando riattivi la presenza
					})
				);
				setErrorField((prev) => prev.filter((item) => item !== row.original.id));

				const debounceKey = `presence_${row.original.id}`;
				debouncedPresenceUpdate(debounceKey, {
					id: areaId,
					date: formattedDate,
					presence: value,
					motivation: null,
					user_id: row.original.id,
					// Mantieni le ore esistenti quando riattivi la presenza
					hours_of_work: row.original.hours_of_work || 0,
					hours_of_cig: row.original.hours_of_cig || 0,
					hours_of_permission: row.original.hours_of_permission || 0,
					hours_of_extra_work: row.original.hours_of_extra_work || 0,
				});
			}
		},
		[row, areaId, formattedDate, setErrorField, setAppealData, debouncedPresenceUpdate, shouldDisable]
	);

	const checkbox = (
		<Checkbox
			defaultChecked={shouldDisable ? false : row?.original?.presence}
			onChange={handlePresenceChange}
			disabled={shouldDisable}
			sx={{
				transform: "scale(1.8)",
				"& .MuiSvgIcon-root": { fontSize: 30 },
			}}
		/>
	);

	if (shouldDisable) {
		return (
			<Tooltip title="Documenti sicurezza non in regola" arrow>
				<span>{checkbox}</span>
			</Tooltip>
		);
	}

	return checkbox;
});

const MealCell = memo(({ row, areaId, formattedDate, setAppealData, debouncedMealUpdate }) => {
	const handleMealChange = useCallback(
		(event, value) => {
			setAppealData(
				produce((draft) => {
					draft[row?.index].meal = value;
				})
			);

			const debounceKey = `meal_${row.original.id}`;
			debouncedMealUpdate(debounceKey, {
				id: areaId,
				date: formattedDate,
				meal: value,
				user_id: row.original.id,
			});
		},
		[row, areaId, formattedDate, setAppealData, debouncedMealUpdate]
	);

	return row?.original?.presence ? (
		<Checkbox
			checked={row?.original?.meal}
			onChange={handleMealChange}
			sx={{
				transform: "scale(1.5)",
				"& .MuiSvgIcon-root": { fontSize: 28 },
			}}
		/>
	) : null;
});

const HoursTextField = memo(({ row, fieldName, errorHoursField, setAppealData, handleChangeHours }) => {
	const handleChange = useCallback(
		(e) => {
			const numericValue = e.target.value === "" ? "" : Number(e.target.value);
			setAppealData(
				produce((draft) => {
					draft[row?.index][fieldName] = numericValue;
				})
			);
			handleChangeHours(e, row);
		},
		[row, fieldName, setAppealData, handleChangeHours]
	);

	return (
		<TextField
			variant="outlined"
			value={row.original[fieldName] || ""}
			onChange={handleChange}
			error={errorHoursField.includes(row.original.id)}
			type="number"
			disabled={!row?.original?.presence}
			inputProps={{ "data-field": fieldName, min: "0" }}
			sx={{ width: "100%" }}
		/>
	);
});

const CigNotesCell = memo(({ row, debouncedSaveCigNotes }) => {
	const handleNotesChange = useCallback(
		(e) => {
			debouncedSaveCigNotes(e.target.value, row.original.id);
		},
		[row.original.id, debouncedSaveCigNotes]
	);

	return (
		<TextField
			variant="outlined"
			multiline
			rows={1}
			defaultValue={row.original.cig_notes || ""}
			onChange={handleNotesChange}
			type="text"
			disabled={!row?.original?.presence}
			sx={{ width: "100%" }}
		/>
	);
});

const MotivationCell = memo(({ row, motivations, areaId, formattedDate, errorField, debouncedMotivationUpdate }) => {
	const handleMotivationChange = useCallback(
		(event, value) => {
			if (!value || !value.id) return;

			// Update local state immediately
			row.original.hours_of_work = 0;
			row.original.hours_of_cig = 0;
			row.original.hours_of_permission = 0;
			row.original.hours_of_extra_work = 0;

			const debounceKey = `motivation_${row.original.id}`;
			debouncedMotivationUpdate(debounceKey, {
				id: areaId,
				date: formattedDate,
				presence: false,
				motivation: value.id,
				user_id: row.original.id,
			});
		},
		[row, areaId, formattedDate, debouncedMotivationUpdate]
	);

	return (
		<Autocomplete
			options={motivations || []}
			key={row.original.id}
			defaultValue={row?.original?.motivation || null}
			getOptionLabel={(option) => option.name}
			multiple={false}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			onChange={handleMotivationChange}
			renderInput={(params) => <TextField {...params} error={errorField.includes(row.original.id)} variant="outlined" />}
		/>
	);
});

export default JournalAppeal;
